import { useState, useEffect } from 'react';
import type { Position, TrailingStopLossConfig } from '../types/mexc';
import { useUpdatePositionTPSL } from '../hooks/useMexcApi';
import { Save, AlertTriangle, Info, CheckCircle, TrendingUp } from 'lucide-react';
import { TrailingStopLossConfig as TSLConfig } from './TrailingStopLossConfig';

interface PositionControlsProps {
  position: Position;
  onClose: () => void;
}

export function PositionControls({ position, onClose }: PositionControlsProps) {
  const [stopLossPrice, setStopLossPrice] = useState<string>('');
  const [takeProfitPrice, setTakeProfitPrice] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'manual' | 'trailing'>('manual');
  const [tslConfig, setTslConfig] = useState<TrailingStopLossConfig | null>(null);

  const updateTPSL = useUpdatePositionTPSL();

  // Get stop orders data using the correct API endpoint
  const [stopOrders, setStopOrders] = useState<any[]>([]);

  useEffect(() => {
    // REVERTED: Simple stop orders fetching (as documented in README.md)
    const fetchStopOrders = async () => {
      try {
        console.log('🔄 PositionControls: Fetching stop orders...');

        // Force server time sync before stop orders API call
        const { syncServerTime } = await import('../utils/crypto');
        console.log('🕐 PositionControls: Forcing fresh server time sync...');
        await syncServerTime();
        console.log('✅ PositionControls: Server time sync completed');

        const mexcApi = (await import('../services/mexcApi')).getMexcApiClient();

        // Use the documented working approach
        console.log('📍 PositionControls: Calling getStopOrders API...');
        const stopOrdersResult = await mexcApi.getStopOrders(position.symbol);
        setStopOrders(stopOrdersResult);

        console.log('✅ PositionControls: Stop Orders fetched successfully:', {
          symbol: position.symbol,
          positionId: position.positionId,
          stopOrdersCount: stopOrdersResult.length,
          stopOrders: stopOrdersResult
        });

      } catch (error) {
        console.error('❌ PositionControls: Error fetching stop orders:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorResponse = (error as any)?.response?.data;
        const errorStatus = (error as any)?.response?.status;
        console.error('❌ PositionControls: Error details:', {
          message: errorMessage,
          response: errorResponse,
          status: errorStatus
        });

        // Set empty array on error to prevent undefined issues
        setStopOrders([]);
      }
    };

    fetchStopOrders();
  }, [position.symbol, position.positionId]);

  // Debug logging for position and stop orders (Enhanced)
  console.log('=== PositionControls Debug (Enhanced) ===');
  console.log('Position Data:', {
    symbol: position.symbol,
    positionId: position.positionId,
    positionType: position.positionType,
    holdAvgPrice: position.holdAvgPrice,
    state: position.state
  });

  console.log('Stop Orders Data (Enhanced):', {
    stopOrdersCount: stopOrders.length,
    hasStopOrders: stopOrders.length > 0,
    authenticationStatus: stopOrders.length > 0 ? '✅ SUCCESS - API working' : '❌ FAILED - Check for 513 errors above',
    stopOrders: stopOrders.map(order => ({
      orderId: order.orderId,
      symbol: order.symbol,
      positionId: order.positionId,
      stopLossPrice: order.stopLossPrice,
      takeProfitPrice: order.takeProfitPrice,
      state: order.state
    }))
  });

  // Find existing stop loss and take profit orders for this position
  const isLong = position.positionType === 1;

  // Find the stop order that matches this position
  const associatedStopOrder = stopOrders.find(order => {
    // Match by positionId (convert both to string for comparison)
    const orderPositionId = String(order.positionId);
    const currentPositionId = String(position.positionId);

    return orderPositionId === currentPositionId &&
           order.symbol === position.symbol &&
           order.state === 1; // Active state
  });

  console.log('Stop Order Analysis:', {
    stopOrdersCount: stopOrders.length,
    associatedStopOrder: associatedStopOrder ? {
      orderId: associatedStopOrder.orderId,
      symbol: associatedStopOrder.symbol,
      positionId: associatedStopOrder.positionId,
      stopLossPrice: associatedStopOrder.stopLossPrice,
      takeProfitPrice: associatedStopOrder.takeProfitPrice,
      state: associatedStopOrder.state
    } : null
  });

  // Extract stop loss and take profit prices from the stop order
  let existingStopLoss = null;
  let existingTakeProfit = null;

  if (associatedStopOrder) {
    if (associatedStopOrder.stopLossPrice && associatedStopOrder.stopLossPrice > 0) {
      existingStopLoss = {
        orderId: associatedStopOrder.orderId,
        price: associatedStopOrder.stopLossPrice,
        side: 'STOP_LOSS',
        state: 'ACTIVE'
      };
    }

    if (associatedStopOrder.takeProfitPrice && associatedStopOrder.takeProfitPrice > 0) {
      existingTakeProfit = {
        orderId: associatedStopOrder.orderId,
        price: associatedStopOrder.takeProfitPrice,
        side: 'TAKE_PROFIT',
        state: 'ACTIVE'
      };
    }
  }

  console.log('Final Results:', {
    existingStopLoss,
    existingTakeProfit
  });
  console.log('=== End Debug ===');

  // Pre-populate fields with existing values when component mounts
  useEffect(() => {
    if (existingStopLoss && !stopLossPrice) {
      setStopLossPrice(existingStopLoss.price.toString());
    }
    if (existingTakeProfit && !takeProfitPrice) {
      setTakeProfitPrice(existingTakeProfit.price.toString());
    }
  }, [existingStopLoss, existingTakeProfit, stopLossPrice, takeProfitPrice, stopOrders]);

  const handleSubmit = () => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    const updateData = {
      positionId: position.positionId,
      stopLossPrice: stopLossPrice ? parseFloat(stopLossPrice) : undefined,
      takeProfitPrice: takeProfitPrice ? parseFloat(takeProfitPrice) : undefined,
    };

    console.log('Submitting stop-loss/take-profit update:', updateData);

    updateTPSL.mutate(updateData, {
      onSuccess: (data) => {
        console.log('🎉 Stop-loss/take-profit update API call successful:', data);
        console.log('📝 Note: Check verification logs above to confirm values were actually updated');
        setShowConfirmation(false);
        setShowSuccess(true);

        // Auto-close after showing success message (extended to allow verification)
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 3000); // Extended to 3 seconds to allow verification logging
      },
      onError: (error) => {
        console.error('❌ Failed to modify position:', error);
        setShowConfirmation(false);
        alert(`Failed to update stop-loss/take-profit: ${error.message}`);
      }
    });
  };

  const currentPrice = position.holdAvgPrice;

  const validateStopLoss = (price: string) => {
    if (!price) return true;
    const numPrice = parseFloat(price);
    if (isLong) {
      return numPrice < currentPrice;
    } else {
      return numPrice > currentPrice;
    }
  };

  const validateTakeProfit = (price: string) => {
    if (!price) return true;
    const numPrice = parseFloat(price);
    if (isLong) {
      return numPrice > currentPrice;
    } else {
      return numPrice < currentPrice;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(value);
  };

  if (showSuccess) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-3 p-4 bg-success-50 dark:bg-success-900/20 rounded-lg">
          <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
          <div>
            <h4 className="font-medium text-success-800 dark:text-success-200">
              Update Successful
            </h4>
            <p className="text-sm text-success-700 dark:text-success-300">
              Stop-Loss and Take-Profit orders have been updated successfully.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-3 p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400" />
          <div>
            <h4 className="font-medium text-warning-800 dark:text-warning-200">
              Confirm Changes
            </h4>
            <p className="text-sm text-warning-700 dark:text-warning-300">
              Are you sure you want to modify this position?
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Symbol:</span>
            <span className="font-medium text-gray-900 dark:text-white">{position.symbol}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Current Price:</span>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(currentPrice)}</span>
          </div>
          {stopLossPrice && (
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">New Stop Loss:</span>
              <span className="font-medium text-danger-600 dark:text-danger-400">
                {formatCurrency(parseFloat(stopLossPrice))}
              </span>
            </div>
          )}
          {takeProfitPrice && (
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">New Take Profit:</span>
              <span className="font-medium text-success-600 dark:text-success-400">
                {formatCurrency(parseFloat(takeProfitPrice))}
              </span>
            </div>
          )}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => setShowConfirmation(false)}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateTPSL.isPending}
            className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {updateTPSL.isPending ? 'Updating...' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <button
          onClick={() => setActiveTab('manual')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'manual'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Save className="w-4 h-4" />
          <span>Manual SL/TP</span>
        </button>
        <button
          onClick={() => setActiveTab('trailing')}
          className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'trailing'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          <span>Trailing Stop-Loss</span>
          {tslConfig?.enabled && (
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          )}
        </button>
      </div>

      {/* Position Info */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Side</p>
          <p className={`font-medium ${
            isLong ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
          }`}>
            {isLong ? 'Long' : 'Short'}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Size</p>
          <p className="font-medium text-gray-900 dark:text-white">
            {position.holdVol.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Entry Price</p>
          <p className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(position.openAvgPrice)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Current Price</p>
          <p className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(currentPrice)}
          </p>
        </div>
      </div>

      {/* Current SL/TP Display */}
      {(existingStopLoss || existingTakeProfit) && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center mb-3">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Current Stop Loss & Take Profit
            </h4>
          </div>
          <div className="space-y-2 text-sm">
            {existingStopLoss && (
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Stop Loss:</span>
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {formatCurrency(existingStopLoss.price)}
                </span>
              </div>
            )}
            {existingTakeProfit && (
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Take Profit:</span>
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {formatCurrency(existingTakeProfit.price)}
                </span>
              </div>
            )}
            {position.liquidatePrice && (
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Liquidation Price:</span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  {formatCurrency(position.liquidatePrice)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'manual' ? (
        <div className="space-y-6">
          {/* Manual Stop Loss/Take Profit Controls */}
          {/* Stop Loss Input */}
          <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {existingStopLoss ? 'Update Stop Loss Price' : 'Set Stop Loss Price'}
        </label>
        <input
          type="number"
          step="0.000001"
          value={stopLossPrice}
          onChange={(e) => setStopLossPrice(e.target.value)}
          placeholder={existingStopLoss
            ? `Current: ${formatCurrency(existingStopLoss.price)} - Enter new price`
            : `Enter stop loss price (${isLong ? 'below' : 'above'} current price)`
          }
          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            stopLossPrice && !validateStopLoss(stopLossPrice)
              ? 'border-danger-300 dark:border-danger-600'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        />
        {stopLossPrice && !validateStopLoss(stopLossPrice) && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
            Stop loss must be {isLong ? 'below' : 'above'} current price
          </p>
        )}
      </div>

      {/* Take Profit Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {existingTakeProfit ? 'Update Take Profit Price' : 'Set Take Profit Price'}
        </label>
        <input
          type="number"
          step="0.000001"
          value={takeProfitPrice}
          onChange={(e) => setTakeProfitPrice(e.target.value)}
          placeholder={existingTakeProfit
            ? `Current: ${formatCurrency(existingTakeProfit.price)} - Enter new price`
            : `Enter take profit price (${isLong ? 'above' : 'below'} current price)`
          }
          className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            takeProfitPrice && !validateTakeProfit(takeProfitPrice)
              ? 'border-danger-300 dark:border-danger-600'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        />
        {takeProfitPrice && !validateTakeProfit(takeProfitPrice) && (
          <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
            Take profit must be {isLong ? 'above' : 'below'} current price
          </p>
        )}
      </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                (!stopLossPrice && !takeProfitPrice) ||
                (stopLossPrice && !validateStopLoss(stopLossPrice)) ||
                (takeProfitPrice && !validateTakeProfit(takeProfitPrice)) ||
                updateTPSL.isPending
              }
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>Update Position</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Trailing Stop-Loss Configuration */}
          <TSLConfig
            position={position}
            onConfigChange={setTslConfig}
          />

          {/* Close Button for TSL Tab */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
